import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { TDocumentDefinitions } from "pdfmake/interfaces";
import logo from "../../asset/KALAMATA_LOGO.png";

(pdfMake as unknown as { vfs: typeof pdfFonts }).vfs = pdfFonts;

/* ------------------ TYPES ------------------ */
interface InvoiceItem {
  category?: string;
  sub_category?: string;
  product?: string;
  qty_packed?: number | string | null;
  qty_unpacked?: number | string | null;
  packed?: number | string | null;
  unit?: string;
  msrp?: number | string | null;
  hst?: number | string | null;
  invoice_no?: string | number;
  invoice_date?: string;
  order_no?: string | number;
  account_no?: string;
  store?: string;
  client_name?: string;
  first_name?: string;
  last_name?: string;
  address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
}

/* =====================================================
 * CONFIG
 * ===================================================== */

/*
 * Keep page 1 separate because it has the large invoice header
 * and may also contain Notes.
 */
const FIRST_PAGE_ROWS = 20;

/*
 * Change ONLY this value later if you want more/fewer rows
 * on page 2, page 3, etc.
 */
const CONTINUATION_PAGE_ROWS = 23;

const PAYMENT_DETAILS = {
  institution: "001",
  transit: "24642",
  accountNumber: "1973036",
  eTransfer: "info@kalamata.ca",
};

const TERMS_AND_CONDITIONS = [
  "Goods remain the property of Asadi Inc until full payment is received.",
  "Shortages, damages or discrepancies must be reported within 3 business days.",
  "Prices and availability may change without prior notice.",
];

/* =====================================================
 * HELPERS
 * ===================================================== */

const toNumber = (value: number | string | null | undefined): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMoney = (value: number): string =>
  `$${value.toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * Formats displayed quantities to a maximum of 2 decimal places.
 * This affects display only and does not round calculation inputs.
 */
const formatNumber = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return "";
  }

  return parsed.toLocaleString("en-CA", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

/**
 * Returns the MSRP of one individual unit from the full-pack MSRP.
 */
const calculatePricePerUnit = (packPrice: number, packed: number): number => {
  if (packed <= 0) {
    return packPrice;
  }

  return packPrice / packed;
};

const formatPercent = (value: number): string =>
  `${Number.isInteger(value) ? value : value.toFixed(2)}%`;

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Page 1 can have a different row count from all continuation pages.
 */
const paginateInvoiceItems = <T>(
  items: T[],
  firstPageSize: number,
  continuationPageSize: number,
): T[][] => {
  if (items.length === 0) {
    return [];
  }

  const pages: T[][] = [];
  pages.push(items.slice(0, firstPageSize));

  for (
    let index = firstPageSize;
    index < items.length;
    index += continuationPageSize
  ) {
    pages.push(items.slice(index, index + continuationPageSize));
  }

  return pages;
};

/* ------------------ IMAGE CONVERTER ------------------ */
const convertImageToBase64 = async (url: string): Promise<string> => {
  const response = await fetch(url);
  const blob = await response.blob();

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => resolve(reader.result as string);

    reader.onerror = reject;

    reader.readAsDataURL(blob);
  });
};

/* =====================================================
 * MAIN FUNCTION
 * ===================================================== */

export const clientInvoice = async (
  payload: InvoiceItem[],
  discount: number,
  notes?: string,
) => {
  if (payload.length === 0) {
    return;
  }

  const imageDataUrl = await convertImageToBase64(logo);
  const invoiceData = payload[0];
  /* ------------------ DISCOUNT ------------------ */
  const rawDiscount = toNumber(discount);
  const discountPercentage = Math.max(0, Math.min(rawDiscount, 100));
  const discountMultiplier = 1 - discountPercentage / 100;

  /* ------------------ CLIENT ------------------ */
  const fullName = [invoiceData.first_name, invoiceData.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  const clientName = invoiceData.client_name || fullName || "N/A";
  const invoiceNumber = invoiceData.invoice_no || "N/A";
  const invoiceDate = invoiceData.invoice_date || formatDate(new Date());

  /* =====================================================
   * TOTAL CALCULATIONS
   * ===================================================== */

  /* Before discount / before tax */
  const subtotalBeforeDiscount = payload.reduce((total, item) => {
    const msrp = toNumber(item.msrp);
    const qtyPacked = toNumber(item.qty_packed);
    const qtyUnpacked = toNumber(item.qty_unpacked);
    const packed = toNumber(item.packed);
    const unitMsrp = calculatePricePerUnit(msrp, packed);

    return total + qtyPacked * msrp + qtyUnpacked * unitMsrp;
  }, 0);

  /* Dollar discount */
  const discountAmount = subtotalBeforeDiscount * (discountPercentage / 100);

  /* After discount / before tax */
  const netValue = payload.reduce((total, item) => {
    const msrp = toNumber(item.msrp);
    const qtyPacked = toNumber(item.qty_packed);
    const qtyUnpacked = toNumber(item.qty_unpacked);
    const packed = toNumber(item.packed);
    const unitMsrp = calculatePricePerUnit(msrp, packed);
    const lineValue = qtyPacked * msrp + qtyUnpacked * unitMsrp;

    return total + lineValue * discountMultiplier;
  }, 0);

  /* HST */
  const taxValue = payload.reduce((total, item) => {
    const msrp = toNumber(item.msrp);
    const qtyPacked = toNumber(item.qty_packed);
    const qtyUnpacked = toNumber(item.qty_unpacked);
    const packed = toNumber(item.packed);
    const hst = toNumber(item.hst);
    const unitMsrp = calculatePricePerUnit(msrp, packed);
    const lineValue = qtyPacked * msrp + qtyUnpacked * unitMsrp;
    const discountedValue = lineValue * discountMultiplier;

    return total + discountedValue * (hst / 100);
  }, 0);

  /* Final amount */
  const invoiceTotal = netValue + taxValue;

  /* =====================================================
   * PRODUCT TABLE BODY
   * ===================================================== */

  const createTableBody = (
    items: InvoiceItem[],
    startingIndex: number,
  ): any[][] => [
    [
      {
        text: "No",
        style: "tableHeader",
      },
      {
        text: "Product",
        style: "tableHeader",
      },
      {
        text: "Price",
        style: "tableHeader",
        alignment: "left" as const,
      },
      {
        text: "Disc",
        style: "tableHeader",
        alignment: "left" as const,
      },
      {
        text: "Disc Price",
        style: "tableHeader",
        alignment: "left" as const,
      },
      {
        text: "HST\n(%)",
        style: "tableHeader",
        alignment: "left" as const,
      },
      {
        text: "HST\n($)",
        style: "tableHeader",
        alignment: "left" as const,
      },
      {
        text: "Qty",
        style: "tableHeader",
        alignment: "left" as const,
      },
      {
        text: "Total\nAfter Tax",
        style: "tableHeader",
        alignment: "right" as const,
      },
    ],

    ...items.map((item, index) => {
      const globalIndex = startingIndex + index;
      const qtyPacked = toNumber(item.qty_packed);
      const qtyUnpacked = toNumber(item.qty_unpacked);
      const packed = toNumber(item.packed);
      const msrp = toNumber(item.msrp);
      const hst = toNumber(item.hst);
      const unitMsrp = calculatePricePerUnit(msrp, packed);
      const lineValueBeforeDiscount = qtyPacked * msrp + qtyUnpacked * unitMsrp;
      const unitPriceBeforeDiscount = msrp;
      const unitPriceAfterDiscount =
        unitPriceBeforeDiscount * discountMultiplier;
      const totalBeforeTax = lineValueBeforeDiscount * discountMultiplier;
      const hstAmount = totalBeforeTax * (hst / 100);
      const totalAfterTax = totalBeforeTax + hstAmount;
      const fillColor = globalIndex % 2 === 0 ? "#ffffff" : "#f7f7f7";
      const productDescription = [
        item.product,
        packed > 0 ? formatNumber(packed) : "",
        item.unit || "",
      ]
        .filter(Boolean)
        .join(" ");

      return [
        {
          text: `${globalIndex + 1}`,
          style: "productRow",
          fillColor,
        },
        {
          text: productDescription,
          style: "productRow",
          fillColor,
        },
        {
          text: formatMoney(unitPriceBeforeDiscount),
          style: "numberCell",
          alignment: "left" as const,
          fillColor,
        },

        {
          text: formatPercent(discountPercentage),
          style: "discountCell",
          alignment: "left" as const,
          fillColor,
        },

        {
          text: formatMoney(unitPriceAfterDiscount),
          style: "numberCell",
          alignment: "left" as const,
          fillColor,
        },
        {
          text: formatPercent(hst),
          style: "taxSubText",
          alignment: "left" as const,
          fillColor,
        },
        {
          text: formatMoney(hstAmount),
          style: "numberCell",
          alignment: "left" as const,
          fillColor,
        },
        {
          stack: [
            {
              text: `${formatNumber(qtyPacked)} pack${qtyPacked === 1 ? "" : "s"}`,
              style: "qtyPrimary",
            },
            ...(qtyUnpacked > 0
              ? [
                  {
                    text: `${formatNumber(qtyUnpacked)} ${
                      item.unit?.trim().toUpperCase() || "unit"
                    }`,
                    style: "qtySecondary",
                  },
                ]
              : []),
          ],
          alignment: "left" as const,
          fillColor,
        },
        {
          text: formatMoney(totalAfterTax),
          style: "afterTaxCell",
          alignment: "right" as const,
          fillColor,
        },
      ];
    }),
  ];

  /* =====================================================
   * PRODUCT TABLE
   * ===================================================== */

  const createProductTable = (
    items: InvoiceItem[],
    startingIndex: number,
    pageBreakBefore = false,
  ) => ({
    ...(pageBreakBefore
      ? {
          pageBreak: "before" as const,
        }
      : {}),

    table: {
      headerRows: 1,
      dontBreakRows: true,
      widths: [15, 230, 38, 25, 40, 20, 40, 25, 55],
      body: createTableBody(items, startingIndex),
    },
    layout: {
      hLineWidth: (rowIndex: number) => (rowIndex === 1 ? 0.5 : 0),
      vLineWidth: () => 0,
      hLineColor: () => "#d7d7d7",
      paddingLeft: () => 4,
      paddingRight: () => 4,
      /* ORIGINAL ROW HEIGHT */
      paddingTop: (rowIndex: number) => (rowIndex === 0 ? 8 : 7),
      paddingBottom: (rowIndex: number) => (rowIndex === 0 ? 8 : 7),
    },
    margin: [-10, 5, 0, 0] as [number, number, number, number],
  });

  /* =====================================================
   * TOTALS SECTION
   * ===================================================== */

  /*
   * Outer table keeps the entire totals box together.
   * If there is not enough space, the whole block moves.
   */
  const totalsSection = {
    table: {
      widths: ["*", 280],
      dontBreakRows: true,
      body: [
        [
          {
            text: "",
          },
          {
            table: {
              widths: [175, "*"],
              dontBreakRows: true,
              body: [
                /* SUBTOTAL */
                [
                  {
                    stack: [
                      {
                        text: "SUBTOTAL",
                        style: "totalLabel",
                      },
                      {
                        text: "Before Discount / Before Tax",
                        style: "totalSubLabel",
                      },
                    ],
                  },
                  {
                    text: formatMoney(subtotalBeforeDiscount),
                    style: "totalValue",
                  },
                ],
                /* DISCOUNT */
                [
                  {
                    stack: [
                      {
                        text: `DISCOUNT (${formatPercent(discountPercentage)})`,
                        style: "totalLabel",
                      },
                      {
                        text: "Discount Applied",
                        style: "totalSubLabel",
                      },
                    ],
                  },
                  {
                    text: `-${formatMoney(discountAmount)}`,
                    style: "discountValue",
                  },
                ],
                /* NET VALUE */
                [
                  {
                    stack: [
                      {
                        text: "NET VALUE",
                        style: "totalLabel",
                      },
                      {
                        text: "After Discount / Before Tax",
                        style: "totalSubLabel",
                      },
                    ],
                  },
                  {
                    text: formatMoney(netValue),
                    style: "totalValue",
                  },
                ],
                /* TAX */
                [
                  {
                    stack: [
                      {
                        text: "TAX (HST)",
                        style: "totalLabel",
                      },
                      {
                        text: "Tax on Discounted Value",
                        style: "totalSubLabel",
                      },
                    ],
                  },
                  {
                    text: formatMoney(taxValue),
                    style: "totalValue",
                  },
                ],
                /* FINAL TOTAL */
                [
                  {
                    stack: [
                      {
                        text: "INVOICE TOTAL",
                        style: "invoiceTotalLabel",
                      },
                      {
                        text: "After Discount / After Tax",
                        style: "invoiceTotalSubLabel",
                      },
                    ],
                    fillColor: "#edf3e5",
                  },
                  {
                    text: formatMoney(invoiceTotal),
                    style: "invoiceTotalValue",
                    fillColor: "#edf3e5",
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: () => 0.8,
              vLineWidth: () => 0.8,
              hLineColor: () => "#222222",
              vLineColor: () => "#222222",
              paddingLeft: () => 9,
              paddingRight: () => 9,
              paddingTop: () => 1,
              paddingBottom: () => 1,
            },
          },
        ],
      ],
    },

    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },

    margin: [0, 12, 0, 0] as [number, number, number, number],
  };

  /* =====================================================
   * NOTES - PAGE 1 ONLY
   * ===================================================== */

  const notesContent = notes?.trim()
    ? [
        {
          columns: [
            {
              width: 43,
              text: "Notes:",
              style: "notesLabel",
            },
            {
              width: "*",
              text: notes.trim(),
              style: "notes",
            },
          ],
          margin: [0, 0, 0, 5] as [number, number, number, number],
        },
      ]
    : [];

  /* =====================================================
   * MANUAL PAGINATION
   * ===================================================== */

  const productPages = paginateInvoiceItems(
    payload,
    FIRST_PAGE_ROWS,
    CONTINUATION_PAGE_ROWS,
  );

  const paginatedProductContent: any[] = [];

  let startingIndex = 0;

  productPages.forEach((pageItems, pageIndex) => {
    const isFirstPage = pageIndex === 0;
    const isLastProductPage = pageIndex === productPages.length - 1;

    /*
     * Page 1 follows the existing large header.
     *
     * Page 2+ starts on a fresh page and receives
     * the small continuation header automatically.
     */
    paginatedProductContent.push(
      createProductTable(pageItems, startingIndex, !isFirstPage),
    );
    startingIndex += pageItems.length;

    /*
     * Totals always follow the final product table.
     *
     * If they physically don't fit, pdfMake moves
     * the whole totals block to the next page.
     */
    if (isLastProductPage) {
      paginatedProductContent.push(totalsSection);
    }
  });

  /* =====================================================
   * DOCUMENT
   * ===================================================== */

  const docDefinition: TDocumentDefinitions = {
    pageSize: "LETTER",

    /*
     * Keep enough bottom space for the Terms footer.
     *
     * The final-page payment section is intentionally
     * compact so the same margin can be used everywhere.
     */
    pageMargins: [30, 35, 30, 95],

    /* =================================================
     * CONTINUATION HEADER
     * =================================================
     *
     * Page 1 remains completely unchanged.
     *
     * Pages 2+ receive the smaller Kalamata header.
     */
    header: (currentPage) => {
      if (currentPage === 1) {
        return {
          text: "",
        };
      }

      return {
        margin: [30, 5, 30, 0] as [number, number, number, number],
        stack: [
          {
            columns: [
              {
                width: 40,
                image: imageDataUrl,
                fit: [30, 30],
              },
              {
                width: "*",
                text: `Invoice #${invoiceNumber}`,
                alignment: "right",
                fontSize: 8,
                bold: true,
                color: "#666666",
                margin: [0, 6, 0, 0],
              },
            ],
          },
          {
            canvas: [
              {
                type: "line",
                x1: 0,
                y1: 0,
                x2: 552,
                y2: 0,
                lineWidth: 0.6,
                lineColor: "#5b8f1b",
              },
            ],
            margin: [0, 1, 0, 0],
          },
        ],
      };
    },

    /* =================================================
     * CONTENT
     * ================================================= */
    content: [
      /* =================================================
       * PAGE 1 HEADER - KEEP AS IS
       * ================================================= */
      {
        columns: [
          {
            width: 55,
            image: imageDataUrl,
            fit: [85, 75],
          },
          {
            width: "*",
            stack: [
              {
                text: "Asadi Inc",
                style: "companyName",
              },
              {
                text: "125 Martin Ross Avenue",
                style: "companyInfo",
              },
              {
                text: "North York, ON M3J 2L9",
                style: "companyInfo",
              },
              {
                text: "Phone: (416) 665-3003",
                style: "companyInfo",
              },
              {
                text: "Email: info@kalamataltd.com",
                style: "companyInfo",
              },
            ],
            margin: [5, 4, 0, 0],
          },
          {
            width: 165,
            stack: [
              {
                text: "INVOICE",
                style: "invoiceTitle",
                alignment: "right",
              },
              {
                text: `Invoice #: ${invoiceNumber}`,
                style: "headerInvoiceNumber",
                alignment: "right",
                margin: [0, 7, 0, 0],
              },
              {
                text: `Date: ${invoiceDate}`,
                style: "headerInvoiceDate",
                alignment: "right",
                margin: [0, 5, 0, 0],
              },
              {
                text: `Bill to: ${clientName}`,
                style: "headerInvoiceDate",
                alignment: "right",
                margin: [0, 5, 0, 0],
              },
            ],
          },
        ],
        columnGap: 10,
      },

      /* ------------------ PAGE 1 DIVIDER ------------------ */
      {
        canvas: [
          {
            type: "line",
            x1: 0,
            y1: 0,
            x2: 552,
            y2: 0,
            lineWidth: 1,
            lineColor: "#5b8f1b",
          },
        ],
        margin: [0, 12, 0, 10],
      },

      /* ------------------ NOTES - PAGE 1 ONLY ------------------ */
      ...notesContent,

      /* ------------------ PRODUCT PAGES ------------------ */
      ...paginatedProductContent,
    ],

    /* =====================================================
     * FOOTER
     * ===================================================== */

    footer: (currentPage, pageCount) => {
      const isLastPage = currentPage === pageCount;
      const footerStack: any[] = [];

      /*
       * =============================================
       * TOP DIVIDER
       * =============================================
       */
      footerStack.push({
        canvas: [
          {
            type: "line",
            x1: 0,
            y1: 0,
            x2: 532,
            y2: 0,
            lineWidth: 0.8,
            lineColor: "#5b8f1b",
          },
        ],
        margin: [0, 0, 0, 4],
      });

      /*
       * =============================================
       * LAST PAGE
       *
       * LEFT  = TERMS & CONDITIONS
       * RIGHT = TOTAL + ACCOUNT DETAILS
       * =============================================
       */
      if (isLastPage) {
        footerStack.push({
          table: {
            widths: ["52%", "48%"],

            body: [
              [
                /*
                 * =========================
                 * LEFT SIDE
                 * TERMS & CONDITIONS
                 * =========================
                 */
                {
                  stack: [
                    {
                      text: "TERMS & CONDITIONS",
                      style: "footerTitle",
                      margin: [0, 0, 0, 3],
                    },

                    ...TERMS_AND_CONDITIONS.map((term) => ({
                      text: `• ${term}`,
                      style: "footerText",
                      margin: [0, 0, 0, 1],
                    })),
                  ],

                  border: [false, false, false, false],
                  margin: [0, 0, 12, 0],
                },

                /*
                 * =========================
                 * RIGHT SIDE
                 * TOTAL + ACCOUNT DETAILS
                 * =========================
                 */
                {
                  stack: [
                    /*
                     * TOTAL
                     */

                    /*
                     * PAYMENT TITLE
                     */
                    {
                      text: "PAYMENT / ACCOUNT DETAILS",
                      style: "footerTitle",
                      margin: [0, 0, 0, 2],
                    },

                    /*
                     * INVOICE REFERENCE
                     */
                    {
                      text: [
                        {
                          text: "Reference: ",
                          style: "paymentLabel",
                        },
                        {
                          text: `Invoice #${invoiceNumber}`,
                          color: "red",
                          bold: true,
                        },
                      ],
                      fontSize: 6.5,
                      margin: [0, 0, 0, 4],
                    },

                    /*
                     * ACCOUNT DETAILS
                     */
                    {
                      table: {
                        widths: [52, "*"],

                        body: [
                          [
                            {
                              text: "Institution:",
                              style: "paymentLabel",
                            },
                            {
                              text: PAYMENT_DETAILS.institution,
                              style: "paymentValue",
                            },
                          ],
                          [
                            {
                              text: "Transit:",
                              style: "paymentLabel",
                            },
                            {
                              text: PAYMENT_DETAILS.transit,
                              style: "paymentValue",
                            },
                          ],
                          [
                            {
                              text: "Account:",
                              style: "paymentLabel",
                            },
                            {
                              text: PAYMENT_DETAILS.accountNumber,
                              style: "paymentValue",
                            },
                          ],
                          [
                            {
                              text: "E-Transfer:",
                              style: "paymentLabel",
                            },
                            {
                              text: PAYMENT_DETAILS.eTransfer,
                              style: "paymentValue",
                            },
                          ],
                        ],
                      },

                      layout: {
                        hLineWidth: () => 0,
                        vLineWidth: () => 0,
                        paddingLeft: () => 0,
                        paddingRight: () => 3,
                        paddingTop: () => 1,
                        paddingBottom: () => 1,
                      },
                    },
                  ],

                  border: [false, false, false, false],
                  margin: [12, 0, 0, 0],
                },
              ],
            ],
          },

          layout: {
            hLineWidth: () => 0,
            vLineWidth: () => 0,
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0,
          },
        });
      } else {
        /*
         * =============================================
         * NORMAL PAGES
         * TERMS FULL WIDTH
         * =============================================
         */
        footerStack.push({
          table: {
            widths: ["*"],

            body: [
              [
                {
                  stack: [
                    {
                      text: "TERMS & CONDITIONS",
                      style: "footerTitle",
                      margin: [0, 0, 0, 3],
                    },

                    ...TERMS_AND_CONDITIONS.map((term) => ({
                      text: `• ${term}`,
                      style: "footerText",
                      margin: [0, 0, 0, 1],
                    })),
                  ],

                  border: [false, false, false, false],
                },
              ],
            ],
          },

          layout: {
            hLineWidth: () => 0,
            vLineWidth: () => 0,
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0,
          },
        });
      }

      /*
       * =============================================
       * BOTTOM DIVIDER + PAGE NUMBER
       * =============================================
       */
      footerStack.push(
        {
          canvas: [
            {
              type: "line",
              x1: 0,
              y1: 0,
              x2: 532,
              y2: 0,
              lineWidth: 0.4,
              lineColor: "#d0d0d0",
            },
          ],
          margin: [0, 4, 0, 3],
        },
        {
          text: `Page ${currentPage} of ${pageCount}`,
          alignment: "right",
          fontSize: 6.5,
          color: "#777777",
        },
      );

      return {
        margin: [40, 0, 40, 0] as [number, number, number, number],
        stack: footerStack,
      };
    },

    /* =====================================================
     * STYLES
     * ===================================================== */

    styles: {
      /* ------------------ MAIN HEADER ------------------ */

      companyName: {
        fontSize: 13,
        bold: true,
        color: "#111111",
      },

      companyInfo: {
        fontSize: 9,
        color: "#666666",
        lineHeight: 1.35,
      },

      invoiceTitle: {
        fontSize: 25,
        bold: true,
        color: "#5b8f1b",
      },

      headerInvoiceNumber: {
        fontSize: 10,
        bold: true,
        color: "#111111",
      },

      headerInvoiceDate: {
        fontSize: 9,
        color: "#222222",
      },

      /* ------------------ NOTES ------------------ */

      notesLabel: {
        fontSize: 8,
        bold: true,
      },

      notes: {
        fontSize: 8,
        color: "#666666",
      },

      /* ------------------ PRODUCT TABLE ------------------ */

      tableHeader: {
        fillColor: "#5b8f1b",
        color: "#ffffff",
        bold: true,
        fontSize: 9.5,
      },

      productRow: {
        fontSize: 9,
        color: "#111111",
      },

      numberCell: {
        fontSize: 7.8,
        color: "#111111",
      },

      discountCell: {
        fontSize: 7.8,
        bold: true,
        color: "#5b8f1b",
      },

      afterTaxCell: {
        fontSize: 7.8,
        bold: true,
        color: "#111111",
      },

      taxSubText: {
        fontSize: 6,
        color: "#777777",
        margin: [0, 2, 0, 0],
      },

      qtyPrimary: {
        fontSize: 8,
        bold: true,
        color: "#111111",
      },

      qtySecondary: {
        fontSize: 6,
        color: "#777777",
        margin: [0, 2, 0, 0],
      },

      /* ------------------ TOTALS ------------------ */

      totalLabel: {
        fontSize: 8.5,
        bold: true,
        color: "#111111",
      },

      totalSubLabel: {
        fontSize: 6,
        color: "#777777",
        margin: [0, 1, 0, 0],
      },

      totalValue: {
        fontSize: 8.5,
        bold: true,
        alignment: "right",
        color: "#111111",
      },

      discountValue: {
        fontSize: 8.5,
        bold: true,
        alignment: "right",
        color: "#b42318",
      },

      invoiceTotalLabel: {
        fontSize: 9,
        bold: true,
        color: "#111111",
      },

      invoiceTotalSubLabel: {
        fontSize: 6,
        color: "#5b8f1b",
        margin: [0, 1, 0, 0],
      },

      invoiceTotalValue: {
        fontSize: 10,
        bold: true,
        alignment: "right",
        color: "#5b8f1b",
      },

      /* ------------------ FOOTER ------------------ */

      footerTitle: {
        fontSize: 7,
        bold: true,
        color: "#5b8f1b",
      },

      footerText: {
        fontSize: 6,
        color: "#666666",
        lineHeight: 1.05,
      },

      paymentLabel: {
        fontSize: 6.2,
        bold: true,
        color: "#222222",
      },

      paymentValue: {
        fontSize: 6.2,
        color: "#666666",
      },
    },

    defaultStyle: {
      fontSize: 9,
    },
  };

  /* =====================================================
   * CREATE PDF
   * ===================================================== */

  pdfMake.createPdf(docDefinition).getBlob((blob) => {
    const blobUrl = URL.createObjectURL(blob);

    const newWindow = window.open(blobUrl, "_blank");

    if (!newWindow) {
      pdfMake.createPdf(docDefinition).download(`invoice-${invoiceNumber}.pdf`);
    }

    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 60000);
  });
};
